import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IEvent as IProject } from '@feathr/blackbox';
import { Button, ConfirmModal, showError, Skeleton, toast } from '@feathr/components';
import { ProjectSelect } from '@feathr/extender/components';
import { useCachedBlackboxVersion, useFundraiser } from '@feathr/extender/hooks';
import { useAccount } from '@feathr/report_components';

interface IProps {
  isLoading: boolean;
}

function TipAssignmentModal({ isLoading }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { fundraiser, devClearProject } = useFundraiser();
  const [opened, { open, close }] = useDisclosure(false);
  const [newProjectId, setNewProjectId] = useState<IProject['id']>();
  const { isOnProduction } = useCachedBlackboxVersion();
  const account = useAccount();

  function handleAssignProject(projectId: IProject['id']): void {
    if (!projectId) {
      return;
    }

    setNewProjectId(projectId);
  }

  // TODO: When the form builder is complete, the Modal will need to be updated to link to the form builder.
  async function handleConfirm(): Promise<void> {
    try {
      fundraiser?.set({ media_credit_event: newProjectId });
      const response = await fundraiser?.updateMediaCreditProject();

      if (!response || response.isErrored) {
        throw new Error(t('Failed to update tip assignment'));
      }

      close();
      toast(t('Tip assignment updated'), { type: 'success' });
    } catch (error) {
      showError(error, t);
    }
  }

  /** Not available in production. */
  async function handleDevClearProject(): Promise<void> {
    try {
      await devClearProject();
      toast(t('Project cleared'), { type: 'success' });
      setNewProjectId(undefined);
    } catch (error) {
      showError(error, t);
    } finally {
      close();
    }
  }

  return (
    <>
      {isLoading ? (
        <Skeleton height={32} width={92} />
      ) : (
        <Button onClick={open}>
          {fundraiser?.mediaCreditEvent ? t('Manage') : t('Assign Project')}
        </Button>
      )}
      <ConfirmModal
        allowOverflow={true}
        cancelButtonText={t('Close')}
        confirmButtonText={t('Confirm')}
        confirmDisabled={
          isLoading || !newProjectId || newProjectId === fundraiser?.mediaCreditEvent
        }
        description={t(
          'Incoming tips will be tracked and assigned to the media balance of the selected project.',
        )}
        leftActions={
          !isOnProduction && account.isInternal ? (
            <Button
              disabled={isLoading || !fundraiser?.mediaCreditEvent}
              onClick={handleDevClearProject}
              type={'dangerSecondary'}
            >
              {t('Dev & QA - Clear project')}
            </Button>
          ) : undefined
        }
        onClose={close}
        onConfirm={handleConfirm}
        opened={opened}
        t={t}
        title={t('Select a Project')}
      >
        <ProjectSelect
          isDependencyLoading={isLoading}
          onChangeSingle={handleAssignProject}
          value={newProjectId ?? fundraiser?.mediaCreditEvent}
        />
      </ConfirmModal>
    </>
  );
}

export default observer(TipAssignmentModal);
